import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft, faPlus, faTrash, faCheck, faLink, faCopy,
  faUsers, faListCheck, faFile, faCommentDots, faClockRotateLeft,
  faClock, faSpinner, faCircleCheck, faTriangleExclamation, faBan,
  faArrowUp, faArrowDown, faMinus, faFire, faShareNodes,
  faEye, faDownload, faShield, faGlobe,
} from '@fortawesome/free-solid-svg-icons';
import { FadeUp } from '../components/common/FadeUp';
import { GlassCard } from '../components/common/GlassCard';
import { Button } from '../components/common/Button';
import { Badge } from '../components/common/Badge';
import { Skeleton, SkeletonCard } from '../components/common/Skeleton';
import { Modal } from '../components/common/Modal';
import { FormField } from '../components/common/FormField';
import { useToast } from '../hooks/useToast';
import { useAuth } from '../hooks/useAuth';
import { deals as dealsApi, tasks as tasksApi, documents as docsApi, comments as commentsApi, activity as activityApi, dealMembers } from '../api';
import type { DealDetail as DealDetailType, Task, Document as DocType, Comment, Activity, DealStage, TaskStatus, TaskPriority } from '../types';

const STATUS_ICON: Record<TaskStatus, typeof faClock> = { pending: faClock, in_progress: faSpinner, completed: faCircleCheck, delayed: faTriangleExclamation, cancelled: faBan };
const STATUS_COLOR: Record<TaskStatus, string> = { pending: 'var(--color-text-tertiary)', in_progress: 'var(--color-info)', completed: 'var(--color-success)', delayed: 'var(--color-error)', cancelled: 'var(--color-text-tertiary)' };
const PRIORITY_ICON: Record<TaskPriority, typeof faArrowUp> = { low: faArrowDown, medium: faMinus, high: faArrowUp, urgent: faFire };
const PRIORITY_COLOR: Record<TaskPriority, string> = { low: 'var(--color-text-tertiary)', medium: 'var(--color-info)', high: 'var(--color-warning)', urgent: 'var(--color-error)' };
const STAGE_CONFIG: Record<DealStage, { label: string; color: string }> = {
  discovery: { label: '탐색', color: 'var(--color-info)' }, qualification: { label: '검증', color: 'var(--color-accent)' },
  proposal: { label: '제안', color: 'var(--color-warning)' }, negotiation: { label: '협상', color: 'oklch(0.65 0.18 50)' },
  closed_won: { label: '성사', color: 'var(--color-success)' }, closed_lost: { label: '실패', color: 'var(--color-error)' },
};

function formatCurrency(v: number, c = 'KRW') { return new Intl.NumberFormat('ko-KR', { style: 'currency', currency: c, maximumFractionDigits: 0 }).format(v); }
function timeAgo(date: string) {
  const d = Date.now() - new Date(date).getTime();
  if (d < 60000) return '방금';
  if (d < 3600000) return `${Math.floor(d / 60000)}분 전`;
  if (d < 86400000) return `${Math.floor(d / 3600000)}시간 전`;
  return `${Math.floor(d / 86400000)}일 전`;
}

type Tab = 'tasks' | 'documents' | 'comments' | 'activity' | 'members' | 'room';

export function DealDetail() {
  const { dealId } = useParams<{ dealId: string }>();
  const id = Number(dealId);
  const [deal, setDeal] = useState<DealDetailType | null>(null);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [docs, setDocs] = useState<DocType[]>([]);
  const [commentsList, setComments] = useState<Comment[]>([]);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<Tab>('tasks');
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showMemberModal, setShowMemberModal] = useState(false);
  const [taskForm, setTaskForm] = useState({ title: '', description: '', section_name: '', due_date: '', priority: 'medium' as TaskPriority });
  const [memberForm, setMemberForm] = useState({ email: '', name: '', role: 'viewer', side: 'buyer' });
  const [commentText, setCommentText] = useState('');
  const { addToast } = useToast();
  useAuth(); // ensure authenticated

  const load = useCallback(async () => {
    try {
      const [dealData, taskData, docData] = await Promise.all([
        dealsApi.get(id),
        tasksApi.list(id),
        docsApi.list(id),
      ]);
      setDeal(dealData);
      setTasks(taskData);
      setDocs(docData);
    } catch { addToast('error', '딜을 불러올 수 없습니다'); }
    finally { setLoading(false); }
  }, [id, addToast]);

  useEffect(() => { load(); }, [load]);

  const loadComments = useCallback(async () => {
    try { setComments(await commentsApi.list(id)); } catch {}
  }, [id]);

  const loadActivity = useCallback(async () => {
    try { setActivities(await activityApi.list(id)); } catch {}
  }, [id]);

  useEffect(() => { if (tab === 'comments') loadComments(); }, [tab, loadComments]);
  useEffect(() => { if (tab === 'activity') loadActivity(); }, [tab, loadActivity]);

  const handleCreateTask = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!taskForm.title.trim()) return;
    try {
      await tasksApi.create(id, taskForm);
      addToast('success', '태스크가 생성되었습니다');
      setShowTaskModal(false);
      setTaskForm({ title: '', description: '', section_name: '', due_date: '', priority: 'medium' });
      setTasks(await tasksApi.list(id));
    } catch (err) { addToast('error', err instanceof Error ? err.message : 'Failed'); }
  };

  const handleToggleTask = async (task: Task) => {
    const newStatus: TaskStatus = task.status === 'completed' ? 'pending' : 'completed';
    try {
      await tasksApi.update(id, task.id, { status: newStatus });
      setTasks(prev => prev.map(t => t.id === task.id ? { ...t, status: newStatus } : t));
    } catch { addToast('error', '업데이트 실패'); }
  };

  const handleDeleteTask = async (taskId: number) => {
    try { await tasksApi.remove(id, taskId); setTasks(prev => prev.filter(t => t.id !== taskId)); addToast('success', '태스크가 삭제되었습니다'); } catch { addToast('error', 'Failed'); }
  };

  const handleAddMember = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!memberForm.email.trim()) return;
    try {
      await dealMembers.add(id, memberForm);
      addToast('success', '멤버가 초대되었습니다');
      setShowMemberModal(false);
      setMemberForm({ email: '', name: '', role: 'viewer', side: 'buyer' });
      load();
    } catch (err) { addToast('error', err instanceof Error ? err.message : 'Failed'); }
  };

  const handlePostComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!commentText.trim()) return;
    try {
      await commentsApi.create(id, { body: commentText });
      setCommentText('');
      loadComments();
    } catch { addToast('error', '게시 실패'); }
  };

  const handleStageChange = async (stage: DealStage) => {
    try {
      await dealsApi.update(id, { stage } as Partial<DealDetailType>);
      setDeal(prev => prev ? { ...prev, stage } : null);
      addToast('success', `단계가 ${STAGE_CONFIG[stage].label}(으)로 변경되었습니다`);
    } catch { addToast('error', '단계 변경 실패'); }
  };

  const copyShareLink = () => {
    if (deal?.room) {
      navigator.clipboard.writeText(`${window.location.origin}/room/${deal.room.share_token}`);
      addToast('success', '공유 링크가 복사되었습니다!');
    }
  };

  if (loading) return (
    <main id="main-content" className="container" style={{ paddingTop: 'var(--space-20)' }}>
      <Skeleton height="2rem" width="200px" style={{ marginBottom: 'var(--space-4)' }} />
      <Skeleton height="1rem" width="400px" style={{ marginBottom: 'var(--space-8)' }} />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 'var(--space-4)' }}>
        <SkeletonCard /><SkeletonCard /><SkeletonCard />
      </div>
    </main>
  );

  if (!deal) return <main id="main-content" className="container" style={{ paddingTop: 'var(--space-20)' }}><p>딜을 찾을 수 없습니다</p></main>;

  const tasksBySection = tasks.reduce((acc, t) => { (acc[t.section_name] ??= []).push(t); return acc; }, {} as Record<string, Task[]>);
  const completedTasks = tasks.filter(t => t.status === 'completed').length;
  const progress = tasks.length ? Math.round((completedTasks / tasks.length) * 100) : 0;

  const TABS: { key: Tab; label: string; icon: typeof faListCheck; count?: number }[] = [
    { key: 'tasks', label: '태스크', icon: faListCheck, count: tasks.length },
    { key: 'documents', label: '문서', icon: faFile, count: docs.length },
    { key: 'members', label: '멤버', icon: faUsers, count: deal.members.length },
    { key: 'comments', label: '댓글', icon: faCommentDots },
    { key: 'activity', label: '활동', icon: faClockRotateLeft },
    { key: 'room', label: '룸', icon: faGlobe },
  ];

  return (
    <main id="main-content" role="main" className="container" style={{ paddingTop: 'var(--space-20)', paddingBottom: 'var(--space-16)' }}>
      {/* Header */}
      <FadeUp>
        <Link to="/deals" style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-2)', color: 'var(--color-text-tertiary)', fontSize: 'var(--text-sm)', marginBottom: 'var(--space-4)', textDecoration: 'none' }}>
          <FontAwesomeIcon icon={faArrowLeft} /> 딜 목록으로
        </Link>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 'var(--space-4)', marginBottom: 'var(--space-6)' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', marginBottom: 'var(--space-2)' }}>
              <h1 style={{ fontSize: 'var(--text-3xl)' }}>{deal.name}</h1>
              <Badge color={STAGE_CONFIG[deal.stage].color}>{STAGE_CONFIG[deal.stage].label}</Badge>
            </div>
            <p style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--text-sm)' }}>
              {deal.company_name && `${deal.company_name} · `}{formatCurrency(deal.value, deal.currency)}
              {deal.expected_close_date && ` · 클로징: ${deal.expected_close_date}`}
            </p>
          </div>
          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            <Button variant="secondary" size="sm" icon={<FontAwesomeIcon icon={faCopy} />} onClick={copyShareLink}>공유</Button>
            <Button variant="secondary" size="sm" icon={<FontAwesomeIcon icon={faShareNodes} />} onClick={() => setShowMemberModal(true)}>초대</Button>
          </div>
        </div>
      </FadeUp>

      {/* Stage Pipeline */}
      <FadeUp delay={0.05}>
        <div style={{ display: 'flex', gap: '2px', marginBottom: 'var(--space-8)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
          {(Object.entries(STAGE_CONFIG) as [DealStage, { label: string; color: string }][]).map(([key, cfg]) => {
            const isActive = key === deal.stage;
            const isPast = Object.keys(STAGE_CONFIG).indexOf(key) < Object.keys(STAGE_CONFIG).indexOf(deal.stage);
            return (
              <button
                key={key}
                onClick={() => handleStageChange(key)}
                style={{
                  flex: 1,
                  padding: 'var(--space-2) var(--space-1)',
                  fontSize: 'var(--text-xs)',
                  fontWeight: 600,
                  border: 'none',
                  cursor: 'pointer',
                  background: isActive ? cfg.color : isPast ? `color-mix(in oklch, ${cfg.color} 25%, transparent)` : 'var(--color-bg-secondary)',
                  color: isActive ? 'white' : isPast ? cfg.color : 'var(--color-text-tertiary)',
                  transition: 'all var(--duration-fast)',
                }}
                aria-current={isActive ? 'step' : undefined}
              >
                {cfg.label}
              </button>
            );
          })}
        </div>
      </FadeUp>

      {/* Progress + Stats */}
      <FadeUp delay={0.1}>
        <div className="deal-stats" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 'var(--space-4)', marginBottom: 'var(--space-8)' }}>
          <GlassCard>
            <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-tertiary)', marginBottom: 'var(--space-2)' }}>태스크 진행률</div>
            <div style={{ fontSize: 'var(--text-2xl)', fontWeight: 700, fontFamily: 'var(--font-display)', marginBottom: 'var(--space-3)' }}>{progress}%</div>
            <div style={{ height: '4px', borderRadius: 'var(--radius-full)', background: 'var(--color-bg-secondary)', overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${progress}%`, borderRadius: 'var(--radius-full)', background: 'var(--color-accent)', transition: 'width 0.6s var(--ease-out-expo)' }} />
            </div>
            <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)', marginTop: 'var(--space-2)' }}>{tasks.length} 중 {completedTasks} 완료</div>
          </GlassCard>
          <GlassCard>
            <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-tertiary)', marginBottom: 'var(--space-2)' }}>문서</div>
            <div style={{ fontSize: 'var(--text-2xl)', fontWeight: 700, fontFamily: 'var(--font-display)' }}>{docs.length}</div>
            <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)', marginTop: 'var(--space-2)' }}>파일 공유됨</div>
          </GlassCard>
          <GlassCard>
            <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-tertiary)', marginBottom: 'var(--space-2)' }}>팀</div>
            <div style={{ fontSize: 'var(--text-2xl)', fontWeight: 700, fontFamily: 'var(--font-display)' }}>{deal.members.length}</div>
            <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)', marginTop: 'var(--space-2)' }}>멤버</div>
          </GlassCard>
          <GlassCard>
            <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-tertiary)', marginBottom: 'var(--space-2)' }}>딜 가치</div>
            <div style={{ fontSize: 'var(--text-2xl)', fontWeight: 700, fontFamily: 'var(--font-display)' }}>{formatCurrency(deal.value, deal.currency)}</div>
            <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)', marginTop: 'var(--space-2)' }}>{deal.currency}</div>
          </GlassCard>
        </div>
      </FadeUp>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 'var(--space-1)', marginBottom: 'var(--space-6)', borderBottom: '1px solid var(--color-border-subtle)', overflowX: 'auto' }}>
        {TABS.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            aria-selected={tab === t.key}
            role="tab"
            style={{
              padding: 'var(--space-3) var(--space-4)',
              fontSize: 'var(--text-sm)',
              fontWeight: 500,
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              color: tab === t.key ? 'var(--color-accent)' : 'var(--color-text-tertiary)',
              borderBottom: tab === t.key ? '2px solid var(--color-accent)' : '2px solid transparent',
              whiteSpace: 'nowrap',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
            }}
          >
            <FontAwesomeIcon icon={t.icon} />
            {t.label}
            {t.count !== undefined && <span style={{ fontSize: 'var(--text-xs)', opacity: 0.7 }}>({t.count})</span>}
          </button>
        ))}
      </div>

      {/* Tab Content: Tasks */}
      {tab === 'tasks' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 'var(--space-4)' }}>
            <Button variant="primary" size="sm" icon={<FontAwesomeIcon icon={faPlus} />} onClick={() => setShowTaskModal(true)}>태스크 추가</Button>
          </div>
          {Object.entries(tasksBySection).length === 0 ? (
            <GlassCard style={{ textAlign: 'center', padding: 'var(--space-10)' }}>
              <p style={{ color: 'var(--color-text-tertiary)' }}>아직 태스크가 없습니다. 첫 태스크를 추가해 보세요.</p>
            </GlassCard>
          ) : Object.entries(tasksBySection).map(([section, sectionTasks]) => (
            <div key={section} style={{ marginBottom: 'var(--space-6)' }}>
              <h3 style={{ fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--color-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 'var(--space-3)' }}>{section}</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                {sectionTasks.map(task => (
                  <GlassCard key={task.id} as="article" style={{ padding: 'var(--space-4)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                      <button
                        onClick={() => handleToggleTask(task)}
                        aria-label={task.status === 'completed' ? 'Mark incomplete' : 'Mark complete'}
                        style={{
                          width: '1.5rem', height: '1.5rem', borderRadius: 'var(--radius-full)', flexShrink: 0,
                          border: `2px solid ${STATUS_COLOR[task.status]}`, background: task.status === 'completed' ? STATUS_COLOR[task.status] : 'transparent',
                          cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '10px',
                        }}
                      >
                        {task.status === 'completed' && <FontAwesomeIcon icon={faCheck} />}
                      </button>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-1)' }}>
                          <span style={{ fontWeight: 500, textDecoration: task.status === 'completed' ? 'line-through' : 'none', opacity: task.status === 'completed' ? 0.6 : 1 }}>{task.title}</span>
                          <FontAwesomeIcon icon={PRIORITY_ICON[task.priority]} style={{ fontSize: '10px', color: PRIORITY_COLOR[task.priority] }} />
                        </div>
                        <div style={{ display: 'flex', gap: 'var(--space-3)', fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)' }}>
                          <Badge color={STATUS_COLOR[task.status]}><FontAwesomeIcon icon={STATUS_ICON[task.status]} /> {task.status}</Badge>
                          {task.due_date && <span>{task.due_date}</span>}
                          {task.assignee_name && <span>{task.assignee_name}</span>}
                        </div>
                      </div>
                      <button onClick={() => handleDeleteTask(task.id)} aria-label="Delete task" style={{ background: 'none', border: 'none', color: 'var(--color-text-tertiary)', cursor: 'pointer', padding: 'var(--space-1)' }}>
                        <FontAwesomeIcon icon={faTrash} style={{ fontSize: 'var(--text-xs)' }} />
                      </button>
                    </div>
                  </GlassCard>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Tab Content: Documents */}
      {tab === 'documents' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 'var(--space-3)' }}>
          {docs.length === 0 ? (
            <GlassCard style={{ textAlign: 'center', padding: 'var(--space-10)' }}>
              <p style={{ color: 'var(--color-text-tertiary)' }}>아직 문서가 없습니다.</p>
            </GlassCard>
          ) : docs.map(doc => (
            <GlassCard key={doc.id} as="article" style={{ padding: 'var(--space-4)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                <FontAwesomeIcon icon={faFile} style={{ color: 'var(--color-accent)' }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{doc.name}</div>
                  <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)' }}>
                    {doc.file_type} &middot; {(doc.file_size / 1048576).toFixed(1)} MB &middot; v{doc.version}
                    {doc.uploaded_by_name && ` · ${doc.uploaded_by_name}`}
                  </div>
                </div>
                <Badge color={doc.status === 'approved' ? 'var(--color-success)' : doc.status === 'pending_approval' ? 'var(--color-warning)' : 'var(--color-text-tertiary)'}>{doc.status}</Badge>
              </div>
            </GlassCard>
          ))}
        </div>
      )}

      {/* Tab Content: Members */}
      {tab === 'members' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 'var(--space-4)' }}>
            <Button variant="primary" size="sm" icon={<FontAwesomeIcon icon={faPlus} />} onClick={() => setShowMemberModal(true)}>초대</Button>
          </div>
          <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
            {deal.members.map(m => (
              <GlassCard key={m.id} style={{ padding: 'var(--space-4)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                  <div style={{
                    width: '2.5rem', height: '2.5rem', borderRadius: 'var(--radius-full)', flexShrink: 0,
                    background: 'var(--color-accent-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontWeight: 700, color: 'var(--color-accent)', fontSize: 'var(--text-sm)',
                  }}>
                    {(m.name || m.email)[0].toUpperCase()}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 500 }}>{m.name || m.email}</div>
                    <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)' }}>{m.email}</div>
                  </div>
                  <Badge color={m.side === 'seller' ? 'var(--color-accent)' : 'var(--color-info)'}>{m.side}</Badge>
                  <Badge color={m.role === 'owner' ? 'var(--color-warning)' : 'var(--color-text-tertiary)'}>{m.role}</Badge>
                </div>
              </GlassCard>
            ))}
          </div>
        </div>
      )}

      {/* Tab Content: Comments */}
      {tab === 'comments' && (
        <div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)', marginBottom: 'var(--space-6)' }}>
            {commentsList.map(c => (
              <GlassCard key={c.id} style={{ padding: 'var(--space-4)' }}>
                <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
                  <div style={{ width: '2rem', height: '2rem', borderRadius: 'var(--radius-full)', background: 'var(--color-accent-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, color: 'var(--color-accent)', fontSize: 'var(--text-xs)', flexShrink: 0 }}>
                    {(c.user_name || '?')[0].toUpperCase()}
                  </div>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-1)' }}>
                      <span style={{ fontWeight: 600, fontSize: 'var(--text-sm)' }}>{c.user_name}</span>
                      <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)' }}>{timeAgo(c.created_at)}</span>
                    </div>
                    <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>{c.body}</p>
                  </div>
                </div>
              </GlassCard>
            ))}
          </div>
          <form onSubmit={handlePostComment} style={{ display: 'flex', gap: 'var(--space-3)' }}>
            <input
              value={commentText}
              onChange={e => setCommentText(e.target.value)}
              placeholder="댓글을 작성하세요..."
              aria-label="댓글"
              style={{
                flex: 1, padding: 'var(--space-3) var(--space-4)', fontSize: 'var(--text-sm)', fontFamily: 'var(--font-sans)',
                color: 'var(--color-text-primary)', background: 'var(--color-surface)', border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-md)', outline: 'none', boxSizing: 'border-box',
              }}
            />
            <Button type="submit" variant="primary" size="sm">게시</Button>
          </form>
        </div>
      )}

      {/* Tab Content: Activity */}
      {tab === 'activity' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          {activities.map(a => (
            <GlassCard key={a.id} style={{ padding: 'var(--space-3) var(--space-4)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', fontSize: 'var(--text-sm)' }}>
                <FontAwesomeIcon icon={faClockRotateLeft} style={{ color: 'var(--color-text-tertiary)' }} />
                <span><strong>{a.user_name}</strong> {a.action} a {a.entity_type}</span>
                <span style={{ marginLeft: 'auto', fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)' }}>{timeAgo(a.created_at)}</span>
              </div>
            </GlassCard>
          ))}
          {activities.length === 0 && <GlassCard style={{ textAlign: 'center', padding: 'var(--space-10)' }}><p style={{ color: 'var(--color-text-tertiary)' }}>아직 활동이 없습니다.</p></GlassCard>}
        </div>
      )}

      {/* Tab Content: Room Settings */}
      {tab === 'room' && deal.room && (
        <GlassCard>
          <h3 style={{ fontSize: 'var(--text-lg)', marginBottom: 'var(--space-6)' }}>
            <FontAwesomeIcon icon={faGlobe} style={{ color: 'var(--color-accent)', marginRight: 'var(--space-2)' }} />
            딜 룸 설정
          </h3>
          <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 'var(--space-3)', background: 'var(--color-bg-secondary)', borderRadius: 'var(--radius-md)' }}>
              <div>
                <div style={{ fontWeight: 500, fontSize: 'var(--text-sm)' }}><FontAwesomeIcon icon={faLink} style={{ marginRight: 'var(--space-2)', color: 'var(--color-accent)' }} />공유 링크</div>
                <code style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)' }}>{window.location.origin}/room/{deal.room.share_token}</code>
              </div>
              <Button variant="ghost" size="sm" onClick={copyShareLink}><FontAwesomeIcon icon={faCopy} /></Button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 'var(--space-3)' }}>
              {[
                { icon: faDownload, label: '다운로드 허용', value: deal.room.allow_download },
                { icon: faShield, label: 'NDA 필수', value: deal.room.require_nda },
                { icon: faEye, label: '워터마크', value: deal.room.watermark },
              ].map(item => (
                <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', padding: 'var(--space-3)', background: 'var(--color-bg-secondary)', borderRadius: 'var(--radius-md)' }}>
                  <FontAwesomeIcon icon={item.icon} style={{ color: item.value ? 'var(--color-success)' : 'var(--color-text-tertiary)' }} />
                  <span style={{ fontSize: 'var(--text-sm)' }}>{item.label}</span>
                  <Badge color={item.value ? 'var(--color-success)' : 'var(--color-text-tertiary)'}>{item.value ? '켜짐' : '꺼짐'}</Badge>
                </div>
              ))}
            </div>
          </div>
        </GlassCard>
      )}

      {/* Create Task Modal */}
      <Modal open={showTaskModal} onClose={() => setShowTaskModal(false)} title="태스크 추가" footer={
        <><Button variant="ghost" onClick={() => setShowTaskModal(false)}>취소</Button><Button variant="primary" onClick={handleCreateTask}>생성</Button></>
      }>
        <form onSubmit={handleCreateTask}>
          <FormField label="제목" required value={taskForm.title} onChange={e => setTaskForm(p => ({ ...p, title: (e.target as HTMLInputElement).value }))} />
          <FormField label="설명" as="textarea" value={taskForm.description} onChange={e => setTaskForm(p => ({ ...p, description: (e.target as HTMLTextAreaElement).value }))} />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)' }}>
            <FormField label="섹션" value={taskForm.section_name} onChange={e => setTaskForm(p => ({ ...p, section_name: (e.target as HTMLInputElement).value }))} placeholder="예: 탐색" />
            <FormField label="우선순위" as="select" value={taskForm.priority} onChange={e => setTaskForm(p => ({ ...p, priority: (e.target as HTMLSelectElement).value as TaskPriority }))}>
              <option value="low">낮음</option><option value="medium">보통</option><option value="high">높음</option><option value="urgent">긴급</option>
            </FormField>
          </div>
          <FormField label="마감일" type="date" value={taskForm.due_date} onChange={e => setTaskForm(p => ({ ...p, due_date: (e.target as HTMLInputElement).value }))} />
        </form>
      </Modal>

      {/* Invite Member Modal */}
      <Modal open={showMemberModal} onClose={() => setShowMemberModal(false)} title="멤버 초대" footer={
        <><Button variant="ghost" onClick={() => setShowMemberModal(false)}>취소</Button><Button variant="primary" onClick={handleAddMember}>초대</Button></>
      }>
        <form onSubmit={handleAddMember}>
          <FormField label="이메일" required type="email" value={memberForm.email} onChange={e => setMemberForm(p => ({ ...p, email: (e.target as HTMLInputElement).value }))} />
          <FormField label="이름" value={memberForm.name} onChange={e => setMemberForm(p => ({ ...p, name: (e.target as HTMLInputElement).value }))} />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)' }}>
            <FormField label="역할" as="select" value={memberForm.role} onChange={e => setMemberForm(p => ({ ...p, role: (e.target as HTMLSelectElement).value }))}>
              <option value="viewer">뷰어</option><option value="collaborator">협업자</option>
            </FormField>
            <FormField label="소속" as="select" value={memberForm.side} onChange={e => setMemberForm(p => ({ ...p, side: (e.target as HTMLSelectElement).value }))}>
              <option value="buyer">바이어</option><option value="seller">셀러</option>
            </FormField>
          </div>
        </form>
      </Modal>

      <style>{`
        @media (min-width: 768px) { .deal-stats { grid-template-columns: repeat(4, 1fr) !important; } }
      `}</style>
    </main>
  );
}
